import { computed, onBeforeUnmount, onMounted, Ref, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCashInPaymentMethods } from "../../composables/useCashInPaymentMethods";
import { TypeKushkiResponse } from "@/types/PaymentTypes";
import dayjs from '@/lib/dayjs';
import { KUSHKI_STATUS_LIST } from "../../constants/kushki.constant";
import { currencyConfigFormat } from "@/helpers/formatCurrency";

export const useKushkiTransactionStatus = () => {
  const router = useRouter();
  const { query } = useRoute();

  const isLoading = ref(false);
  const detailKushki = ref<TypeKushkiResponse>({} as TypeKushkiResponse);

  const { getKushkiTransferData } = useCashInPaymentMethods();

  const detailKushkiStatus = computed(() => {
    if (!detailKushki.value.txStatus) return '';

    return (
      KUSHKI_STATUS_LIST[detailKushki.value.processorState] ||
      detailKushki.value.processorState
    );
  });

  const detailKushkiDate = computed(() => {
    if (!detailKushki.value.txDate) return '';
    return dayjs(detailKushki.value.txDate).format('DD/MMM/YYYY · hh:mm a');
  });

  const amount = computed(() => {
    return currencyConfigFormat({
      amount: Number(detailKushki.value.amount || 0),
      currencyCode: 'COP',
      hasCode: true,
      hasSymbol: true,
    })
  }
  );

  const checkStatusInterval: Ref<ReturnType<typeof setTimeout> | undefined> =
    ref();

  function clearStatusInterval() {
    if (checkStatusInterval.value) {
      clearTimeout(checkStatusInterval.value);
      checkStatusInterval.value = undefined;
    }
  }

  async function handleKushkiTransferData() {
    isLoading.value = true;
    const tokenKushki = query?.token || '';

    if (!tokenKushki) {
      isLoading.value = false;
      return;
    }
    const data = await getKushkiTransferData({
      token: String(tokenKushki)
    });

    if (data) {
      const kushkiBankName = localStorage.getItem('kushkiBankName') ?? '';

      detailKushki.value = {
        ...data,
        bankName: kushkiBankName
      };

      clearStatusInterval();

      if (data.processorState === 'PENDING') {
        checkStatusInterval.value = setTimeout(() => {
          handleKushkiTransferData();
        }, 60000);
      }
    }

    isLoading.value = false;
  }

  function onHome() {
    clearStatusInterval();
    localStorage.removeItem('kushkiBankName');
    router.push({ name: 'movements' });
  }

  onMounted(async () => {
    await handleKushkiTransferData();
  });

  onBeforeUnmount(() => {
    clearStatusInterval();
  });

  return {
    amount,
    onHome,
    detailKushki,
    detailKushkiDate,
    detailKushkiStatus,
    isLoading,
  };
};